import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LogOut, RefreshCw, ChevronDown, Search, Mail, Phone, Check } from "lucide-react";
import { LOGO } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const apiFetch = async (path, options = {}) => {
  let res = await fetch(`${API}${path}`, { credentials: "include", ...options });
  if (res.status === 401) {
    const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
    if (r.ok) res = await fetch(`${API}${path}`, { credentials: "include", ...options });
  }
  return res;
};

const inputCls =
  "w-full border-b border-forest/25 bg-transparent py-3 text-sm text-ink placeholder:text-forest-mist/50 outline-none transition-colors duration-300 focus:border-gold";

function LoginCard({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      onLogin(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md border border-sand bg-ivory p-10 md:p-12"
        data-testid="admin-login-card"
      >
        <div className="flex flex-col items-center text-center">
          <img src={LOGO} alt="Mirevaa Journeys" className="h-16 w-16 rounded-full object-cover" />
          <p className="mt-5 font-display text-2xl tracking-[0.14em] text-forest">MIREVAA</p>
          <p className="mt-1 text-[10px] uppercase tracking-widest2 text-gold">Enquiry Inbox</p>
        </div>
        <form onSubmit={submit} className="mt-10 space-y-7" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="text-[10px] uppercase tracking-widest2 font-medium text-forest">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@mirevaajourneys.com"
              className={`${inputCls} mt-1.5`}
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-[10px] uppercase tracking-widest2 font-medium text-forest">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              className={`${inputCls} mt-1.5`}
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-xs text-red-700">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={busy}
            className="w-full bg-forest py-4 text-[11px] uppercase tracking-widest2 font-medium text-ivory transition-colors duration-500 hover:bg-gold hover:text-forest disabled:opacity-60"
          >
            {busy ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function EnquiryCard({ e, index, onToggle }) {
  const [open, setOpen] = useState(false);
  const isNew = e.status === "new";
  const date = e.created_at
    ? new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : "";

  return (
    <div
      data-testid={`enquiry-row-${index}`}
      className="border border-sand bg-ivory transition-colors hover:border-gold/50"
    >
      <button
        data-testid={`enquiry-expand-${index}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full flex-col gap-3 p-5 text-left md:flex-row md:items-center md:justify-between md:gap-6 md:px-7"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <p className="font-display text-xl text-forest">{e.name}</p>
            <span
              data-testid={`enquiry-status-${index}`}
              className={`px-2.5 py-1 text-[9px] uppercase tracking-widest2 font-medium ${
                isNew ? "bg-gold/15 text-gold" : "bg-forest/10 text-forest-mist"
              }`}
            >
              {isNew ? "New" : "Contacted"}
            </span>
          </div>
          <p className="mt-1 truncate text-xs text-forest-mist">
            {e.destination} · {e.holiday_type} · {e.travellers}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-5">
          <p className="text-[11px] text-forest-mist">{date}</p>
          <ChevronDown className={`h-4 w-4 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="border-t border-sand px-5 py-6 md:px-7"
          data-testid={`enquiry-detail-${index}`}
        >
          <div className="grid grid-cols-1 gap-x-10 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div className="flex items-center gap-2.5 text-forest">
              <Mail className="h-3.5 w-3.5 text-gold" />
              <a href={`mailto:${e.email}`} className="hover:text-gold">
                {e.email}
              </a>
            </div>
            <div className="flex items-center gap-2.5 text-forest">
              <Phone className="h-3.5 w-3.5 text-gold" />
              {e.phone}
            </div>
            <Detail label="Budget" value={e.budget} />
            <Detail label="Travel Dates" value={e.travel_dates} />
            <Detail label="Travellers" value={e.travellers} />
            <Detail label="Type of Journey" value={e.holiday_type} />
          </div>
          {e.special_requirements && (
            <p className="mt-5 text-sm text-forest-mist">
              <span className="text-[10px] uppercase tracking-widest2 text-gold">Special Requirements — </span>
              {e.special_requirements}
            </p>
          )}
          {e.message && (
            <p className="mt-3 text-sm leading-relaxed text-forest-mist">
              <span className="text-[10px] uppercase tracking-widest2 text-gold">Message — </span>
              {e.message}
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <button
              data-testid={`enquiry-mark-contacted-${index}`}
              onClick={() => onToggle(e)}
              className={`inline-flex items-center gap-2 px-6 py-3 text-[10px] uppercase tracking-widest2 font-medium transition-colors duration-500 ${
                isNew
                  ? "bg-forest text-ivory hover:bg-gold hover:text-forest"
                  : "border border-forest/25 text-forest hover:border-gold hover:text-gold"
              }`}
            >
              <Check className="h-3.5 w-3.5" />
              {isNew ? "Mark as Contacted" : "Mark as New"}
            </button>
            <a
              data-testid={`enquiry-reply-${index}`}
              href={`mailto:${e.email}?subject=Your Mirevaa Journey Enquiry`}
              className="inline-flex items-center gap-2 border border-forest/25 px-6 py-3 text-[10px] uppercase tracking-widest2 font-medium text-forest transition-colors duration-500 hover:border-gold hover:text-gold"
            >
              <Mail className="h-3.5 w-3.5" />
              Reply by Email
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
}

const Detail = ({ label, value }) => (
  <div className="text-forest">
    <span className="text-[10px] uppercase tracking-widest2 text-gold">{label} — </span>
    <span className="text-forest-mist">{value}</span>
  </div>
);

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    apiFetch("/auth/me")
      .then((res) => (res.ok ? res.json() : false))
      .then(setUser)
      .catch(() => setUser(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/admin/enquiries");
      if (res.status === 401) {
        setUser(false);
        return;
      }
      setEnquiries(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false);
  };

  const toggle = async (e) => {
    const next = e.status === "new" ? "contacted" : "new";
    await apiFetch(`/admin/enquiries/${e.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    setEnquiries((list) => list.map((x) => (x.id === e.id ? { ...x, status: next } : x)));
  };

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p className="font-display text-2xl italic text-forest-mist">Loading...</p>
      </div>
    );
  }
  if (user === false) return <LoginCard onLogin={setUser} />;

  const filtered = enquiries.filter((e) => {
    const q = query.toLowerCase();
    return [e.name, e.email, e.destination, e.holiday_type].join(" ").toLowerCase().includes(q);
  });
  const newCount = enquiries.filter((e) => e.status === "new").length;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-forest">
        <div className="mx-auto flex h-20 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="Mirevaa Journeys" className="h-11 w-11 rounded-full object-cover" />
            <div>
              <p className="font-display text-lg tracking-[0.14em] text-ivory">ENQUIRY INBOX</p>
              <p className="text-[9px] uppercase tracking-widest2 text-gold">{user.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="flex h-10 w-10 items-center justify-center border border-ivory/20 text-ivory/70 transition-colors hover:border-gold hover:text-gold"
              aria-label="Refresh enquiries"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex h-10 w-10 items-center justify-center border border-ivory/20 text-ivory/70 transition-colors hover:border-gold hover:text-gold"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10 md:py-14">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-widest2 text-gold">Journey Enquiries</p>
            <h1 className="mt-2 font-display text-3xl text-forest md:text-4xl">
              {enquiries.length} {enquiries.length === 1 ? "enquiry" : "enquiries"}
              <span className="italic text-gold"> · {newCount} new</span>
            </h1>
          </div>
          <div className="relative md:w-72">
            <Search className="pointer-events-none absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-forest-mist" />
            <input
              data-testid="admin-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, destination..."
              className={`${inputCls} pl-7`}
            />
          </div>
        </div>

        <div className="mt-10 space-y-4" data-testid="enquiry-list">
          {filtered.length === 0 && (
            <p className="border border-sand bg-ivory p-10 text-center text-sm text-forest-mist" data-testid="enquiry-empty">
              {enquiries.length === 0
                ? "No enquiries yet. New journey enquiries from the website will appear here."
                : "No enquiries match your search."}
            </p>
          )}
          {filtered.map((e, i) => (
            <EnquiryCard key={e.id} e={e} index={i} onToggle={toggle} />
          ))}
        </div>
      </main>
    </div>
  );
}
